import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  faLongArrowAltRight,
  faLongArrowAltUp,
} from '@fortawesome/free-solid-svg-icons';
import { assert, isDefined } from '@sama/js-core';
import { forOwn, round } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { Raycaster } from 'three';
import { setCssProperty } from '../../../cross-team-shared/misc-utils';
import { assertDefined } from '../../../cross-team-shared/misc-utils/strict-record';
import { shellConnectorToken } from '../../../team-apis/core/shell-connector';
import { SideViewRotationService } from '../../misc-services/side-view-rotation.service';
import { SideViewCamera } from '../../render/cameras/side-view-camera';
import { SideViewCamerasService } from '../../render/cameras/side-view-cameras.service';
import { SideViewResizeControls } from '../../render/controls/side-view-resize-controls';
import { CuboidService } from '../../render/mesh/cuboid/cuboid.service';
import { RenderTriggerService } from '../../render/render-trigger.service';
import {
  getSideViewRendererLayers,
  RendererLayer,
  setRendererLayers,
} from '../../render/render-utils';
import { SceneControllerService } from '../../render/scene-controller.service';
import { PointCloudContentStore } from '../../state/point-cloud-content-store';
import { AbstractSideViewDragHandler } from '../../tools/abstract-side-view-drag-handler';
import { SideViewRescaleCuboidDragHandler } from '../../tools/side-view-rescale-cuboid-drag-handler';
import { SideViewRotateCuboidDragHandler } from '../../tools/side-view-rotate-cuboid-drag-handler';
import { SideViewTranslateCuboidDragHandler } from '../../tools/side-view-translate-cuboid-drag-handler';
import { getFaceDimensions } from '../../utils/cuboid-utils';
import { isLeftButton } from '../../utils/misc-utils';
import { getMouseVector } from '../../utils/point-cloud-utils';

export type Axis = 0 | 1 | 2; // X, Y, Z

/**
 * This class represents a side view being viewed in a 3 js canvas
 * The html template for this class will actually display the component
 * and is mainly used for handling mouse events.  Each view represents
 * the camera with a given offset axis that the camera will show a 2d
 * view from.  The scene and the active mesh that are actually displayed
 * are stored with the side view helper.
 */
@Component({
  selector: 'h3-side-view',
  templateUrl: './side-view.component.html',
  styleUrls: ['./side-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SideViewComponent
  extends DirectiveSuperclass
  implements OnInit, AfterViewInit, OnDestroy
{
  @Input({ required: true }) axis!: Axis;

  private readonly sceneControllerService = inject(SceneControllerService);
  private readonly sideViewRotationService = inject(SideViewRotationService);
  private readonly store = inject(PointCloudContentStore);
  private readonly elementRef = inject(ElementRef);
  private readonly cuboidService = inject(CuboidService);
  private readonly sideViewCamerasService = inject(SideViewCamerasService);
  private readonly renderTriggerService = inject(RenderTriggerService);
  private readonly injector = inject(Injector);

  dimensions$!: Observable<string>;
  title$!: Observable<string>;

  faLongArrowAltRight = faLongArrowAltRight;
  faLongArrowAltUp = faLongArrowAltUp;
  isReadOnly$!: Observable<boolean>;
  isLeftEditPanel$ = this.store('editPanel')('location').$.pipe(
    map((location) => location === 'left'),
  );

  axis$ = this.getInput$('axis');

  private controls!: SideViewResizeControls;
  private rendererLayer!: RendererLayer;
  private shellConnector = inject(shellConnectorToken);

  @HostListener('contextmenu', ['$event'])
  openContextmenu(event: MouseEvent) {
    if (this.isOverMesh(event)) {
      this.shellConnector.openShapeContextMenu([event.clientX, event.clientY]);
    } else {
      this.shellConnector.openCanvasContextMenu([event.clientX, event.clientY]);
    }
  }

  @HostListener('wheel', ['$event'])
  handleMouseEvent(event: WheelEvent) {
    event.preventDefault();

    const dollyFactor = -Math.sign(event.deltaY) * 0.1;
    this.sideViewCamerasService.dollyCamera(this.axis, dollyFactor);
    this.renderTriggerService.trigger();
  }

  @HostListener('mousedown', ['$event'])
  protected onMouseDown(event: MouseEvent) {
    if (!isLeftButton(event) || this.store.state().readOnly) {
      return;
    }
    const handler = this.getDragHandler(event);
    if (!handler) {
      return;
    }
    handler.startListening();
  }

  @HostListener('mousemove', ['$event'])
  protected onMouseMove(event: MouseEvent) {
    this.controls.onMouseMove(event);
  }

  ngOnInit() {
    this.rendererLayer = getSideViewRendererLayers()[this.axis];

    this.dimensions$ = this.store.focusedShapeLocation$.pipe(
      filter(isDefined),
      map((cuboid) =>
        getFaceDimensions(cuboid.scale, this.axis)
          .map((val) => round(val, 2))
          .join(' x '),
      ),
    );
    this.title$ = this.sideViewRotationService.getViewTitle$(this.axis);

    this.isReadOnly$ = this.store('readOnly').$;
  }

  ngAfterViewInit() {
    this.buildControls();
    this.setUpCameraSubscription();
    this.setUpCursorSubscription();
    this.setUpControlsVisibilitySubscription();
    this.store
      .getViewRegionStore(this.axis)('outputName')
      .set(this.store.state().output.name);
  }

  ngOnDestroy() {
    this.controls.dispose();
    this.sceneControllerService.removeFromScene(this.controls);
  }

  private getDragHandler(event: MouseEvent) {
    let handler: AbstractSideViewDragHandler | undefined;
    const shouldRotate = event.shiftKey;

    if (shouldRotate) {
      handler = new SideViewRotateCuboidDragHandler(
        event,
        this.controls,
        this.injector,
      );
      this.sideViewRotationService.setDragInProgress(true);
      void handler.completion.then(() =>
        this.sideViewRotationService.setDragInProgress(false),
      );
    } else if (this.isOverMesh(event)) {
      handler = new SideViewTranslateCuboidDragHandler(
        event,
        this.controls,
        this.injector,
      );
    } else if (this.controls.isOverControls(event)) {
      handler = new SideViewRescaleCuboidDragHandler(
        event,
        this.controls,
        this.injector,
      );
    }

    return handler;
  }

  private isOverMesh(event: MouseEvent) {
    const mouse = getMouseVector(event, this.elementRef.nativeElement);
    const raycaster = new Raycaster();
    raycaster.layers.set(this.rendererLayer);
    raycaster.setFromCamera(
      mouse,
      this.sideViewCamerasService.getCameraByAxis(this.axis),
    );
    const focusedShapeIndex = this.store.getFocusedShapeIndex();
    assert(focusedShapeIndex, 'Side panels require focused shape');
    const focusedMesh = this.cuboidService.getHelper(focusedShapeIndex);
    assert(focusedMesh, 'Focused shape should have corresponding mesh');
    return raycaster.intersectObject(focusedMesh.box, false).length > 0;
  }

  private buildControls() {
    const camera = this.sideViewCamerasService.getCameraByAxis(this.axis);
    this.controls = new SideViewResizeControls(
      !this.store.state().readOnly,
      this.axis,
      camera,
      this.injector,
    );
    this.controls.domElement = this.elementRef.nativeElement;
    this.controls.plane.add(camera);

    setRendererLayers([this.rendererLayer], this.controls);
    this.sceneControllerService.addToScene(this.controls);
  }

  private setUpCameraSubscription() {
    this.subscribeTo(
      combineLatest([
        this.cuboidService.focusedCuboid$.pipe(filter(isDefined)),
        this.store('render')('currentEditSource').$,
        this.store.getViewRegionStore(this.axis).$,
      ]),
      ([focusedCuboid, editSource, viewRegion]) => {
        assertDefined(viewRegion);
        const camera = this.sideViewCamerasService.getCameraByAxis(this.axis);
        assert(
          camera instanceof SideViewCamera,
          'SideViewComponent should only be used without the "new-edit-panel" featureflag',
        );
        camera.updateLocation(
          focusedCuboid,
          editSource,
          this.controls,
          viewRegion.size,
        );
      },
    );
  }

  private setUpCursorSubscription() {
    this.subscribeTo(
      this.sideViewRotationService.getRotationCursor$(),
      (cursor) => {
        setCssProperty(this.elementRef, '--h3-panel-cursor', cursor);
      },
    );
  }

  private setUpControlsVisibilitySubscription() {
    this.subscribeTo(
      this.sideViewRotationService.getIsInRotationMode$(),
      (isInRotationMode) => {
        forOwn(this.controls.arrows, (arrow) => {
          setRendererLayers(
            isInRotationMode ? [] : [this.rendererLayer],
            arrow,
          );
        });
        this.renderTriggerService.trigger();
      },
    );
  }
}
